import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone, CalendarCheck, UserRound } from "lucide-react";
import Logo from "./Logo";
import { scrollToId } from "../lib/scroll";
import { CONTACT } from "../lib/data";
import { useLang } from "../lib/i18n";
import { useAuth } from "../lib/auth";

const ORDER = ["home", "about", "services", "packages", "gallery", "contact"];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { lang, setLang, t, a } = useLang();
  const { user } = useAuth();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    setTimeout(() => scrollToId(id), open ? 250 : 0);
  };

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-[#0A0B0E]/85 backdrop-blur-xl border-b border-white/10"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <nav className="max-w-7xl mx-auto px-5 sm:px-8 h-[72px] flex items-center justify-between">
        <button data-testid="nav-brand-logo" onClick={() => go("home")} className="shrink-0" aria-label="OGO home">
          <Logo />
        </button>

        <div className="hidden lg:flex items-center gap-8">
          {ORDER.map((id) => (
            <button
              key={id}
              data-testid={`nav-link-${id}`}
              onClick={() => go(id)}
              className="font-mono text-[11px] uppercase tracking-[0.22em] text-slate-300 hover:text-gold transition-colors duration-300"
            >
              {t.nav.links[id]}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-3">
          <button
            data-testid="nav-lang-toggle"
            onClick={() => setLang(lang === "en" ? "te" : "en")}
            className="rounded-full border border-gold/40 bg-gold/5 px-3.5 py-2 font-mono text-[11px] font-semibold tracking-[0.15em] text-gold hover:bg-gold hover:text-ink transition-colors duration-300"
          >
            {lang === "en" ? "TE" : "EN"}
          </button>
          <Link
            data-testid="nav-login-link"
            to={user ? "/dashboard" : "/login"}
            className="hidden md:inline-flex items-center gap-2 rounded-full border border-white/20 text-white font-mono text-[10px] uppercase tracking-[0.18em] px-4 py-2.5 hover:border-gold hover:text-gold transition-colors duration-300"
          >
            <UserRound size={14} /> {user ? a.nav.dashboard : a.nav.login}
          </Link>
          <Link
            data-testid="nav-book-online-button"
            to="/book"
            className="hidden sm:inline-flex items-center gap-2 rounded-full bg-gold text-ink font-semibold text-sm px-5 py-2.5 hover:bg-white transition-colors duration-300"
          >
            <CalendarCheck size={15} strokeWidth={2.4} />
            {a.nav.book}
          </Link>
          <a
            data-testid="nav-call-now-button"
            href={CONTACT.tel}
            className="hidden xl:inline-flex items-center gap-2 rounded-full border border-gold/50 text-gold font-semibold text-sm px-5 py-2.5 hover:bg-gold hover:text-ink transition-colors duration-300"
          >
            <Phone size={15} strokeWidth={2.4} />
            {CONTACT.phoneDisplay}
          </a>
          <button
            data-testid="nav-mobile-toggle"
            className="lg:hidden text-white p-2"
            onClick={() => setOpen((o) => !o)}
            aria-label="Toggle menu"
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="lg:hidden overflow-hidden bg-[#0A0B0E]/95 backdrop-blur-xl border-b border-white/10"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {ORDER.map((id) => (
                <button
                  key={id}
                  data-testid={`nav-mobile-link-${id}`}
                  onClick={() => go(id)}
                  className="font-display font-bold text-2xl text-white text-left hover:text-gold transition-colors"
                >
                  {t.nav.links[id]}
                </button>
              ))}
              <Link
                data-testid="nav-mobile-book-button"
                to="/book"
                className="mt-2 inline-flex items-center justify-center gap-2 rounded-full bg-gold text-ink font-semibold px-5 py-3"
              >
                <CalendarCheck size={16} strokeWidth={2.4} /> {a.nav.book}
              </Link>
              <Link
                data-testid="nav-mobile-login-link"
                to={user ? "/dashboard" : "/login"}
                className="inline-flex items-center justify-center gap-2 rounded-full border border-white/20 text-white font-semibold px-5 py-3"
              >
                <UserRound size={16} /> {user ? a.nav.dashboard : a.nav.login}
              </Link>
              <a
                data-testid="nav-mobile-call-button"
                href={CONTACT.tel}
                className="inline-flex items-center justify-center gap-2 rounded-full border border-gold/50 text-gold font-semibold px-5 py-3"
              >
                <Phone size={16} strokeWidth={2.4} /> {CONTACT.phoneDisplay}
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
